import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import Editor from '@/components/editor';
import { useCreateInquiry } from '@/services/inquiry/queries';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/auth';
import { usePostHog } from 'posthog-js/react';

const supportForm = z.object({
  subject: z.string().min(1, 'Subject is required'),
  email: z.string().email('Invalid email'),
  message: z.string().min(1, 'Message is required')
});

export default function CreateInquiryForm() {
  const { activeWorkspace } = useAuthStore();
  const [editorKey, setEditorKey] = useState(0);
  const posthog = usePostHog();
  
  const { mutate: createInquiry, isPending } = useCreateInquiry(
    () => {
      posthog?.capture('form_submitted', { form_name: 'create_inquiry', success: true });
      toast.success('Inquiry created successfully');
      form.reset();
      setEditorKey(prev => prev + 1);
    },
    () => {
      posthog?.capture('form_submitted', { form_name: 'create_inquiry', success: false });
      toast.error('Failed to create inquiry');
    }
  );

  const form = useForm<z.infer<typeof supportForm>>({
    resolver: zodResolver(supportForm),
    defaultValues: {
      subject: '',
      email: '',
      message: ''
    }
  });

  const onSubmit = (data: z.infer<typeof supportForm>) => {
    if (!activeWorkspace) {
      toast.error('Please login to submit an inquiry');
      return;
    }
    createInquiry({
      subject: data.subject,
      message: data.message,
      workspaceId: activeWorkspace,
      email: data.email
    });
  };

  return (
    <div className='space-y-6'>
      <div>
        <p className='mt-2 text-gray-500 text-xs'>Reach out to us and we will respond as soon as possible</p>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className='max-w-2xl space-y-6'
        >
          <FormField
            control={form.control}
            name='subject'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-lg font-semibold'>
                  Subject
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder='Enter your subject' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name='email'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-lg font-semibold'>
                  Email
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder='Enter your contact email'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name='message'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-lg font-semibold'>
                  Message
                </FormLabel>
                <FormControl>
                  <Editor key={editorKey} onChange={field.onChange} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <Button
            type='submit'
            isLoading={isPending}
            className='w-fit'
            trackingEvent='inquiry_submitted'
            trackingProperties={{ location: 'help_page' }}
          >
            Submit Inquiry
          </Button>
        </form>
      </Form>
    </div>
  );
}